// src/components/PaginationControls.js
import React, { useMemo } from "react";
import { formatNumber } from "../utils/numberFormat";
import {
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
} from "lucide-react";

/**
 * Cleaner, less "boxed-in" pagination:
 * - No big outer container card
 * - Inline toolbar feel
 * - Only small chips where necessary
 * - RTL-friendly
 */
const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  hasNext,
  hasPrevious,
  onPageChange,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
}) => {
  const rangeLabel = useMemo(() => {
    if (!totalPages) return "—";
    return `صفحه ${formatNumber(currentPage)} از ${formatNumber(totalPages)}`;
  }, [currentPage, totalPages]);

  const goFirst = () => {
    if (currentPage > 1) {
      const synthetic = { target: { value: 1 } };
      onPageChange(synthetic);
    }
  };

  const goLast = () => {
    if (currentPage < totalPages) {
      const synthetic = { target: { value: totalPages } };
      onPageChange(synthetic);
    }
  };

  const IconBtn = ({ disabled, onClick, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="
        hidden sm:inline-flex items-center justify-center
        h-9 w-9 rounded-xl
        text-slate-700
        hover:bg-slate-100
        active:scale-[0.98]
        disabled:opacity-40 disabled:cursor-not-allowed
        transition
      "
    >
      {children}
    </button>
  );

  const GhostBtn = ({ disabled, onClick, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="
        inline-flex items-center gap-2
        h-9 rounded-xl px-3
        text-slate-700
        hover:bg-slate-100
        active:scale-[0.98]
        disabled:opacity-40 disabled:cursor-not-allowed
        transition
      "
    >
      {children}
    </button>
  );

  const SelectBase =
    "h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:ring-4 focus:ring-blue-100";

  return (
    <div
      className="
        w-full
        flex flex-col gap-3
        md:flex-row md:items-center md:justify-between
      "
      dir="rtl"
    >
      {/* Info / selectors (light, no container) */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span className="text-slate-500">تعداد در صفحه</span>
        <select
          id="pageSize"
          className={SelectBase}
          value={pageSize}
          onChange={onPageSizeChange}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {formatNumber(size)}
            </option>
          ))}
        </select>

        <span className="mx-1 hidden md:inline-block text-slate-300">•</span>

        <span className="text-slate-600">{rangeLabel}</span>
      </div>

      {/* Navigation (minimal, airy) */}
      <div className="flex w-full items-center justify-between gap-1 sm:w-auto sm:justify-end sm:gap-1.5">
        <IconBtn disabled={!hasPrevious} onClick={goFirst} title="اولین صفحه">
          <ChevronsRight className="h-4 w-4" />
        </IconBtn>

        <GhostBtn disabled={!hasPrevious} onClick={onPreviousPage} title="قبلی">
          <ChevronRight className="h-4 w-4" />
          <span className="hidden text-sm font-medium sm:inline">قبلی</span>
        </GhostBtn>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-sm text-slate-500">صفحه</span>
          <select
            aria-label="انتخاب صفحه"
            className={`${SelectBase} min-w-[4.5rem]`}
            value={currentPage}
            onChange={onPageChange}
          >
            {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => {
              const val = i + 1;
              return (
                <option key={val} value={val}>
                  {formatNumber(val)}
                </option>
              );
            })}
          </select>
          <span className="text-sm text-slate-500">
            از {formatNumber(Math.max(totalPages, 1))}
          </span>
        </div>

        <button
          type="button"
          onClick={onNextPage}
          disabled={!hasNext}
          className="
            inline-flex items-center gap-2
            h-9 rounded-xl px-3
            bg-slate-900 text-white
            hover:bg-slate-800
            active:scale-[0.98]
            disabled:opacity-40 disabled:cursor-not-allowed
            transition
          "
          title="بعدی"
          aria-label="بعدی"
        >
          <span className="hidden text-sm font-medium sm:inline">بعدی</span>
          <ChevronLeft className="h-4 w-4" />
        </button>

        <IconBtn disabled={!hasNext} onClick={goLast} title="آخرین صفحه">
          <ChevronsLeft className="h-4 w-4" />
        </IconBtn>
      </div>
    </div>
  );
};

export default PaginationControls;
